import { useEffect, useRef, useState } from "react";

/**
 * Small ambient "pause for a breath" widget that sits above the footer on every page.
 * Click to toggle a 4-7-8 breathing cycle. Off by default.
 */
const PHASES = [
  { label: "Inhale", duration: 4000 },
  { label: "Hold", duration: 7000 },
  { label: "Exhale", duration: 8000 },
];

export default function BreathePause() {
  const [active, setActive] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!active) {
      if (timer.current) clearTimeout(timer.current);
      setPhaseIdx(0);
      return;
    }
    timer.current = setTimeout(() => {
      setPhaseIdx((i) => (i + 1) % PHASES.length);
    }, PHASES[phaseIdx].duration);
    return () => timer.current && clearTimeout(timer.current);
  }, [active, phaseIdx]);

  const phase = PHASES[phaseIdx];
  const scale =
    phase.label === "Inhale" ? 1.25 : phase.label === "Exhale" ? 0.85 : 1.1;
  const dur = phase.duration / 1000;

  return (
    <section
      data-testid="breathe-pause"
      aria-label="Pause for a breath"
      className="relative overflow-hidden bg-[var(--cc-bg)] py-20 md:py-24 border-t border-[var(--cc-line)]"
    >
      <span aria-hidden className="cc-orb cc-orb-1" style={{ opacity: 0.25 }} />
      <div className="cc-container relative">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--cc-primary)]">
            A small pause
          </p>
          <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">
            Before you go anywhere — <em className="italic">breathe.</em>
          </h3>
          <p className="mt-4 text-sm md:text-base text-[var(--cc-text-soft)]">
            Tap the circle. Four in, seven hold, eight out. As many rounds as you&apos;d like.
          </p>

          <div className="mt-10 flex flex-col items-center">
            <button
              data-testid="breathe-pause-toggle"
              onClick={() => setActive((s) => !s)}
              aria-pressed={active}
              className="relative w-44 h-44 md:w-52 md:h-52 rounded-full flex items-center justify-center focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--cc-primary)]/30"
            >
              {/* outer ring */}
              <span
                className="absolute inset-0 rounded-full border border-[var(--cc-primary)]/30"
                style={{
                  transform: `scale(${active ? scale : 1})`,
                  transition: `transform ${active ? dur : 0.6}s ease-in-out`,
                }}
              />
              {/* inner core */}
              <span
                className="rounded-full"
                style={{
                  width: "55%",
                  height: "55%",
                  background:
                    "radial-gradient(circle at 35% 30%, #cdd6cf, var(--cc-primary))",
                  boxShadow: "0 10px 40px rgba(132,151,143,0.35)",
                  transform: `scale(${active ? scale : 1})`,
                  transition: `transform ${active ? dur : 0.6}s ease-in-out`,
                }}
              />
              <span
                data-testid="breathe-pause-label"
                className="absolute font-serif text-xl md:text-2xl text-white drop-shadow-sm select-none pointer-events-none"
              >
                {active ? phase.label : "Begin"}
              </span>
            </button>
            <p
              data-testid="breathe-pause-status"
              className="mt-6 text-xs uppercase tracking-[0.22em] text-[var(--cc-text-soft)]"
            >
              {active ? "Following your breath…" : "Tap to start"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
